import React, { useState, useEffect, useMemo } from 'react';
import api, { getImageUrl } from '../config/api';
import PageNavbar from './PageNavbar';
import './Desserts.css';
import './CateringMenu.css';

const TIERS = [
  { value: 'silver', label: 'Silver Menu', bandClass: 'silver' },
  { value: 'gold', label: 'Gold Menu', bandClass: 'gold' },
  { value: 'platinum', label: 'Platinum Menu', bandClass: 'platinum' }
];

const emptyForm = {
  menu_tier: 'silver',
  name: '',
  description: '',
  price: '',
  sort_order: '0',
  active: true
};

const CateringMenu = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [removeImage, setRemoveImage] = useState(false);
  const [copiedCustomerLink, setCopiedCustomerLink] = useState(false);

  const customerPlannerUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/catering-planner` : '/catering-planner';

  const copyPlannerLink = async () => {
    try {
      await navigator.clipboard.writeText(customerPlannerUrl);
      setCopiedCustomerLink(true);
      window.setTimeout(() => setCopiedCustomerLink(false), 2000);
    } catch (_) {
      window.prompt('Copy this customer link:', customerPlannerUrl);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await api.get('/catering/menu-items/list');
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching catering menu:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const byTier = useMemo(() => {
    const map = { silver: [], gold: [], platinum: [] };
    items.forEach((row) => {
      if (map[row.menu_tier]) map[row.menu_tier].push(row);
    });
    return map;
  }, [items]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditing(null);
    setShowForm(false);
    setImageFile(null);
    setImagePreview('');
    setRemoveImage(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setRemoveImage(false);
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImageFile(null);
      setImagePreview('');
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({
      menu_tier: row.menu_tier || 'silver',
      name: row.name || '',
      description: row.description || '',
      price: row.price != null ? String(row.price) : '',
      sort_order: row.sort_order != null ? String(row.sort_order) : '0',
      active: row.active !== false
    });
    setImageFile(null);
    setRemoveImage(false);
    setImagePreview(row.image_path ? getImageUrl(row.image_path) : '');
    setShowForm(true);
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('menu_tier', formData.menu_tier);
    fd.append('name', formData.name.trim());
    if (formData.description.trim()) fd.append('description', formData.description.trim());
    else fd.append('description', '');
    if (formData.price.trim() !== '') fd.append('price', formData.price);
    else fd.append('price', '');
    fd.append('sort_order', formData.sort_order.trim() !== '' ? formData.sort_order : '0');
    fd.append('active', formData.active ? 'true' : 'false');
    if (imageFile) fd.append('image', imageFile);
    if (editing && removeImage) fd.append('remove_image', 'true');
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = buildFormData();
      if (editing) {
        await api.put(`/catering/menu-items/${editing.id}`, fd);
        alert('Menu item updated');
      } else {
        await api.post('/catering/menu-items', fd);
        alert('Menu item added. It will be available for the customer catering builder when you enable that flow.');
      }
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      resetForm();
      fetchItems();
    } catch (error) {
      alert(error.response?.data?.error || (editing ? 'Failed to update item' : 'Failed to add item'));
    }
  };

  const handleToggleActive = async (row) => {
    if (
      !window.confirm(
        `${row.active ? 'Deactivate' : 'Activate'} "${row.name}"? Inactive items stay hidden from customer menus.`
      )
    ) {
      return;
    }
    try {
      const fd = new FormData();
      fd.append('active', (!row.active).toString());
      await api.put(`/catering/menu-items/${row.id}`, fd);
      fetchItems();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update');
    }
  };

  return (
    <div className="desserts-page catering-page">
      <PageNavbar />
      <div className="container">
        <div className="catering-header desserts-header">
          <div>
            <h1>Menu items (admin)</h1>
            <p className="catering-intro">
              Upload dishes with photos and prices under <strong>Silver</strong>, <strong>Gold</strong>, and{' '}
              <strong>Platinum</strong> tiers for your records. The public <strong>CaterCraft</strong> planner uses a
              full preset catalogue — share its link below with guests.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              if (showForm) resetForm();
              else setShowForm(true);
            }}
          >
            {showForm ? 'Cancel' : 'Add menu item'}
          </button>
        </div>

        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>Customer link — CaterCraft</h2>
          <p className="desserts-hint">
            Share this URL for the full public experience: category browsing, per-plate totals, bulk tiers (50–500),
            Silver/Gold/Platinum presets, occasion ideas, print, and a shareable cart link — no login required.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <code style={{ flex: '1', minWidth: '200px', padding: '10px', background: '#f5f5f5', borderRadius: '8px', fontSize: '13px', wordBreak: 'break-all' }}>
              {customerPlannerUrl}
            </code>
            <button type="button" className="btn btn-primary" onClick={copyPlannerLink}>
              {copiedCustomerLink ? 'Copied!' : 'Copy link'}
            </button>
            <a className="btn btn-secondary" href="/catering-planner" target="_blank" rel="noopener noreferrer">
              Open CaterCraft
            </a>
          </div>
        </div>

        {showForm && (
          <div className="card">
            <h2>{editing ? 'Edit menu item' : 'New menu item'}</h2>
            <p className="desserts-hint">
              Choose the package tier, add the dish name, optional description, price in ₹, and a photo. Sort order
              controls listing within the same tier (lower numbers first).
            </p>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Package tier *</label>
                <select
                  className="form-control"
                  value={formData.menu_tier}
                  onChange={(e) => setFormData({ ...formData, menu_tier: e.target.value })}
                  required
                >
                  {TIERS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Dish name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g. Hyderabadi Dum Biryani"
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g. Serves with raita & mirchi ka salan"
                />
              </div>
              <div className="form-group">
                <label>Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g. 180 (per plate or as you price it)"
                />
              </div>
              <div className="form-group">
                <label>Sort order</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label>Photo (optional)</label>
                <div className="catering-form-image-row">
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleImageChange} />
                  {editing && editing.image_path && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={removeImage}
                        onChange={(e) => {
                          setRemoveImage(e.target.checked);
                          if (e.target.checked) {
                            setImageFile(null);
                            if (imagePreview && imagePreview.startsWith('blob:')) {
                              URL.revokeObjectURL(imagePreview);
                            }
                            setImagePreview('');
                          } else {
                            setImagePreview(editing.image_path ? getImageUrl(editing.image_path) : '');
                          }
                        }}
                      />
                      Remove current photo
                    </label>
                  )}
                </div>
                {imagePreview && !removeImage && (
                  <img className="catering-form-preview" src={imagePreview} alt="" />
                )}
              </div>
              {editing && (
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />{' '}
                    Active (visible when customer flow is enabled)
                  </label>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary">
                  {editing ? 'Save changes' : 'Add item'}
                </button>
                {editing && (
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading catering menu…</div>
        ) : (
          TIERS.map((tier) => {
            const rows = byTier[tier.value] || [];
            return (
              <section key={tier.value} className="catering-tier-section">
                <div className={`catering-tier-band ${tier.bandClass}`}>
                  <span>{tier.label}</span>
                  <span className="catering-tier-count">{rows.length} item{rows.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="catering-tier-body">
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: 72 }}>Photo</th>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Price (₹)</th>
                          <th>Sort</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="no-data">
                              No items in {tier.label} yet. Use &quot;Add menu item&quot; and choose this tier.
                            </td>
                          </tr>
                        ) : (
                          rows.map((row) => (
                            <tr key={row.id} className={row.active ? '' : 'row-inactive'}>
                              <td>
                                {row.image_path ? (
                                  <img
                                    className="catering-thumb"
                                    src={getImageUrl(row.image_path)}
                                    alt=""
                                  />
                                ) : (
                                  <div className="catering-thumb-placeholder" aria-hidden>
                                    🍽
                                  </div>
                                )}
                              </td>
                              <td>
                                <strong>{row.name}</strong>
                              </td>
                              <td>{row.description || '—'}</td>
                              <td>{row.price != null ? `₹${parseFloat(row.price).toFixed(2)}` : '—'}</td>
                              <td>{row.sort_order ?? 0}</td>
                              <td>
                                <span className={`status-badge ${row.active ? 'status-active' : 'status-inactive'}`}>
                                  {row.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleEdit(row)}
                                  style={{ padding: '5px 12px', fontSize: '14px', marginRight: '8px' }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${row.active ? 'btn-secondary' : 'btn-success'}`}
                                  onClick={() => handleToggleActive(row)}
                                  style={{ padding: '5px 12px', fontSize: '14px' }}
                                >
                                  {row.active ? 'Deactivate' : 'Activate'}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CateringMenu;
